import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { PageHead } from '@/components/Common/Seo/PageHead';
import { CompaniesDirectoryView } from '@/components/Directory/CompaniesDirectoryView';
import { NO_STORE_CACHE_CONTROL } from '@/constants/cache';
import { SITE_ORIGIN } from '@/constants/site';
import type { CompanySeoRecord } from '@/utils/seo/company-directory';
import {
  getCompanyDirectoryPage,
  getCompanyDirectoryTotalPages,
} from '@/utils/seo/company-directory-data';
import { COMPANIES_PER_PAGE } from '@/utils/seo/constants';

type Props = {
  companies: CompanySeoRecord[];
  hasNextPage: boolean;
  page: number;
  totalPages: number | null;
};

export const getServerSideProps: GetServerSideProps<Props> = async ({ params, res }) => {
  const page = Number(params?.page);

  if (!page || Number.isNaN(page) || page < 1) {
    res.setHeader('Cache-Control', NO_STORE_CACHE_CONTROL);
    return { notFound: true };
  }

  if (page === 1) {
    return {
      redirect: { destination: '/companies', permanent: true },
    };
  }

  try {
    const totalPages = await getCompanyDirectoryTotalPages();

    if (typeof totalPages === 'number' && page > totalPages) {
      res.setHeader('Cache-Control', NO_STORE_CACHE_CONTROL);
      return { notFound: true };
    }

    const { companies, hasNextPage } = await getCompanyDirectoryPage(page);

    if (!companies.length) {
      res.setHeader('Cache-Control', NO_STORE_CACHE_CONTROL);
      return { notFound: true };
    }

    return {
      props: {
        companies,
        hasNextPage,
        page,
        totalPages: totalPages ?? null,
      },
    };
  } catch (error) {
    res.setHeader('Cache-Control', NO_STORE_CACHE_CONTROL);
    throw error;
  }
};
export default function CompaniesPageByNumber({
  companies,
  hasNextPage,
  page,
  totalPages,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const title = `Companies Directory - Page ${page}`;
  const description =
    'Browse companies and find verified contacts for the teams you want to reach.';
  const path = `/companies/page/${page}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@id': `${SITE_ORIGIN}${path}#breadcrumb`,
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', item: `${SITE_ORIGIN}/`, name: 'Home', position: 1 },
          { '@type': 'ListItem', item: `${SITE_ORIGIN}/companies`, name: 'Companies', position: 2 },
          {
            '@type': 'ListItem',
            item: `${SITE_ORIGIN}${path}`,
            name: `Page ${page}`,
            position: 3,
          },
        ],
      },
      {
        '@id': `${SITE_ORIGIN}${path}#itemlist`,
        '@type': 'ItemList',
        itemListElement: companies.map((company, index) => ({
          '@type': 'ListItem',
          item: `${SITE_ORIGIN}/companies/${company.publicId}${company.slug ? `/${company.slug}` : ''}`,
          name: company.name,
          position: index + 1 + (page - 1) * COMPANIES_PER_PAGE,
        })),
        name: `Companies Directory - Page ${page}`,
        numberOfItems: companies.length,
      },
    ],
  };

  return (
    <>
      <PageHead description={description} path={path} robots="noindex, follow" title={title} />

      <Head>
        <link
          href={`${SITE_ORIGIN}${page <= 2 ? '/companies' : `/companies/page/${page - 1}`}`}
          rel="prev"
        />
        {hasNextPage && <link href={`${SITE_ORIGIN}/companies/page/${page + 1}`} rel="next" />}
        <script
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          key="companies-directory-page-ld+json"
          type="application/ld+json"
        />
      </Head>

      <CompaniesDirectoryView
        companies={companies}
        hasNextPage={hasNextPage}
        page={page}
        totalPages={totalPages ?? undefined}
      />
    </>
  );
}
